import React, { useState, useEffect } from 'react';
import { createUserWithEmailAndPassword, sendEmailVerification } from 'firebase/auth';
import { auth, functions } from '../../firebaseConfig';
import { useNavigate, useLocation } from 'react-router-dom';
import { Mail, User, Lock } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { useError } from '../../contexts/ErrorContext';
import Button from '../../components/ui/Button';

const SignupPage = () => {
  const { showError } = useError();
  const [formData, setFormData] = useState({
    nomeCompleto: '',
    email: '',
    senha: '',
    confirmar_senha: '',
    termos: false
  });
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const planData = params.get('plan');
    if (planData) {
      try {
        setSelectedPlan(JSON.parse(decodeURIComponent(planData)));
      } catch (error) {
        console.error('Error parsing plan data:', error);
      }
    }
  }, [location]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.senha !== formData.confirmar_senha) {
      showError('As senhas não coincidem.');
      return;
    }

    setIsLoading(true);
    let userCredential = null;
    try {
      userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.senha);

      await sendEmailVerification(userCredential.user, {
        url: `${window.location.origin}/verify-email`,
        handleCodeInApp: true,
      });

      const registerUserFn = httpsCallable(functions, 'register_user');
      await registerUserFn({
        email: formData.email,
        nomeCompleto: formData.nomeCompleto
      });

      if (selectedPlan && selectedPlan.priceId) {
        const createCheckoutFn = httpsCallable(functions, 'create_checkout_session');
        const result = await createCheckoutFn({
          priceId: selectedPlan.priceId,
          successUrl: `${window.location.origin}/success`,
          cancelUrl: `${window.location.origin}/cancel`,
        });
        window.location.assign(result.data.url);
      } else {
        navigate('/generate-map');
      }

    } catch (error) {
      // Compensation: delete orphaned Auth user so email is freed for retry
      if (userCredential) {
        try { await userCredential.user.delete(); }
        catch (deleteErr) { console.error('Cleanup failed:', deleteErr); }
      }
      showError(error);
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg max-w-md w-full p-8 space-y-6 border border-purple-100 relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 -translate-y-8 translate-x-8 w-64 h-64 bg-gradient-to-br from-purple-200 to-pink-200 rounded-full filter blur-3xl opacity-30 animate-pulse pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 translate-y-8 -translate-x-8 w-64 h-64 bg-gradient-to-br from-purple-200 to-pink-200 rounded-full filter blur-3xl opacity-30 animate-pulse pointer-events-none"></div>

        {/* Header */}
        <div className="text-center space-y-2">
          <div className="flex justify-center">
            <div className="flex items-center gap-2 text-xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              <span>✨</span>
              <span>Mapas Astrais</span>
            </div>
          </div>
          <p className="text-gray-500">Descubra os segredos do seu mapa astral</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700" htmlFor="nomeCompleto">
              Nome Completo
            </label>
            <div className="relative">
              <input
                type="text"
                id="nomeCompleto"
                name="nomeCompleto"
                value={formData.nomeCompleto}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-purple-400 pl-10 text-gray-700"
                placeholder="Seu nome completo"
                required
              />
              <User className="w-5 h-5 text-purple-400 absolute left-2 top-2.5" />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700" htmlFor="email">
              E-mail
            </label>
            <div className="relative">
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-purple-400 pl-10 text-gray-700"
                placeholder="Seu melhor e-mail"
                required
              />
              <Mail className="w-5 h-5 text-purple-400 absolute left-2 top-2.5" />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700" htmlFor="senha">
              Senha
            </label>
            <div className="relative">
              <input
                type="password"
                id="senha"
                name="senha"
                value={formData.senha}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-purple-400 pl-10 text-gray-700"
                placeholder="Crie uma senha"
                required
              />
              <Lock className="w-5 h-5 text-purple-400 absolute left-2 top-2.5" />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700" htmlFor="confirmar-senha">
              Confirmar Senha
            </label>
            <div className="relative">
              <input
                type="password"
                id="confirmar-senha"
                name="confirmar_senha"
                value={formData.confirmar_senha}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-purple-400 pl-10 text-gray-700"
                placeholder="Confirme sua senha"
                required
              />
              <Lock className="w-5 h-5 text-purple-400 absolute left-2 top-2.5" />
            </div>
          </div>

          <div className="flex items-center gap-3 mt-4">
            <input
              type="checkbox"
              id="termos"
              name="termos"
              checked={formData.termos}
              onChange={handleInputChange}
              className="h-5 w-5 shrink-0 text-purple-600 focus:ring-purple-400 border-purple-300 rounded cursor-pointer"
              required
            />
            <span className="text-sm text-gray-700">
              Li e aceito os{' '}
              <a href="/terms-of-use" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-700 underline">
                Termos de Uso
              </a>
              {' '}e a{' '}
              <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-700 underline">
                Pol. de Privacidade
              </a>
            </span>
          </div>

          <Button
            type="submit"
            disabled={isLoading}
            loading={isLoading}
            loadingText="Criando conta..."
            fullWidth
          >
            Criar Conta
          </Button>
        </form>

        <p className="text-center text-sm text-gray-500">
          Já tem uma conta?{' '}
          <a href="/login" className="font-medium text-purple-600 hover:text-purple-700">
            Faça login
          </a>
        </p>
      </div>
    </div>
  );
};

export default SignupPage;